import { useEffect, useState } from 'react';

import { useDisclosure } from 'src/shared/components/ui';

/**
 * Hook for managing caption modal state and navigation
 */
export function useCaptionModal<T extends { id: string; caption?: string }>(
  items: T[]
) {
  const {
    isOpen: isCaptionModalOpen,
    onOpen: onOpenCaptionModal,
    onClose: onCloseCaptionModal
  } = useDisclosure();

  const [currentCaptionIndex, setCurrentCaptionIndex] = useState(0);
  const [captionValue, setCaptionValue] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Sync caption value when current index changes
  useEffect(() => {
    const currentItem = items[currentCaptionIndex];

    if (currentItem) {
      setCaptionValue(currentItem.caption || '');
    } else {
      setCaptionValue('');
    }
  }, [currentCaptionIndex, items]);

  const handleEditCaption = (index: number) => {
    setIsEditMode(true);
    setCurrentCaptionIndex(index);
    setCaptionValue(items[index]?.caption || '');
    onOpenCaptionModal();
  };

  const handleCaptionNext = () => {
    if (currentCaptionIndex < items.length - 1) {
      const nextIndex = currentCaptionIndex + 1;

      setCurrentCaptionIndex(nextIndex);
    } else {
      // All items captioned, close modal
      setIsEditMode(false);
      onCloseCaptionModal();
      setCaptionValue('');
    }
  };

  const handleCaptionPrevious = () => {
    if (currentCaptionIndex > 0) {
      const prevIndex = currentCaptionIndex - 1;

      setCurrentCaptionIndex(prevIndex);
    }
  };

  const handleCaptionDone = () => {
    setIsEditMode(false);
    onCloseCaptionModal();
    setCaptionValue('');
  };

  const openModalForNewItems = (startIndex: number = 0) => {
    setIsEditMode(false);
    setCurrentCaptionIndex(startIndex);
    setCaptionValue(items[startIndex]?.caption || '');
    onOpenCaptionModal();
  };

  return {
    isCaptionModalOpen,
    onOpenCaptionModal,
    onCloseCaptionModal,
    currentCaptionIndex,
    setCurrentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    setIsEditMode,
    handleEditCaption,
    handleCaptionNext,
    handleCaptionPrevious,
    handleCaptionDone,
    openModalForNewItems
  };
}
